import { useCallback, useEffect, useState } from "react";
import { fetchGroupOrderingAvailability } from "../api/groupBasketApi";
import { COUNTRY } from "../config/market";
import useCatalogUpdates from "./useCatalogUpdates";

export const GROUP_ORDERING_EVENT = "groupOrdering:changed";
export const AVAILABILITY_POLL_MS = 30000;
let availabilityRequest = null;
let pending = false;
const listeners = new Set();
const sameMarket = (answer) => !answer?.country || String(answer.country).toUpperCase() === COUNTRY;
const publish = (answer) => listeners.forEach((listener) => listener(sameMarket(answer) ? answer : null));

const loadAvailability = (force = false) => {
  if (availabilityRequest && (!force || pending)) return availabilityRequest;
  pending = true;
  const request = fetchGroupOrderingAvailability().catch(() => null);
  availabilityRequest = request;
  request.then((answer) => {
    // A socket update may have superseded this HTTP request while it ran.
    if (availabilityRequest !== request) return;
    pending = false;
    if (!answer) availabilityRequest = null;
    publish(answer);
  });
  return request;
};

export const forgetGroupOrderingAvailability = () => {
  availabilityRequest = null;
  pending = false;
};

export const groupOrderingOpenFor = (availability, vertical) =>
  Boolean(
    availability?.enabled === true &&
      Array.isArray(availability.enabledVerticals) &&
      availability.enabledVerticals.includes(vertical)
  );

/** Public setting changes also reach shoppers who are signed out. */
export default function useGroupOrderingAvailability(vertical) {
  const [availability, setAvailability] = useState(null);
  const [live, setLive] = useState(false);
  const refresh = useCallback(() => loadAvailability(true), []);

  useEffect(() => {
    listeners.add(setAvailability);
    const request = loadAvailability(true);
    request.then((answer) => {
      if (listeners.has(setAvailability) && availabilityRequest === request) {
        setAvailability(sameMarket(answer) ? answer : null);
      }
    });
    return () => listeners.delete(setAvailability);
  }, []);

  useCatalogUpdates({
    [GROUP_ORDERING_EVENT]: (answer) => {
      if (!answer?.country || !sameMarket(answer)) return;
      // Use the public server answer immediately, including disabling it.
      // Replacing the request prevents a slow, earlier GET undoing the event.
      availabilityRequest = Promise.resolve(answer);
      pending = false;
      publish(answer);
    },
    disconnect: () => setLive(false),
    connect_error: () => setLive(false),
  }, { onConnect: () => { setLive(true); refresh(); } });

  useEffect(() => {
    if (live) return undefined;
    const timer = setInterval(refresh, AVAILABILITY_POLL_MS);
    return () => clearInterval(timer);
  }, [live, refresh]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  return { open: groupOrderingOpenFor(availability, vertical), availability };
}
